import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { AbstractControl, FormBuilder, ValidationErrors, ValidatorFn, FormGroup, Validators } from "@angular/forms";

import { CountryISO, PhoneNumberFormat, SearchCountryField } from "ngx-intl-tel-input";
import { BsDatepickerConfig } from "ngx-bootstrap/datepicker";
import { ReCaptchaV3Service } from "ng-recaptcha";
import { TranslateService } from '@ngx-translate/core';

import { environment } from "../../../../environments/environment";

import { CmsService } from 'src/app/Services/cms.service';
import { ApiService } from "src/app/Services/api.service";
import { ExternalService } from "src/app/Services/external.service";
import { UtilsService } from 'src/app/Services/utils.service';

interface ScheduleData {
  pattern: { id: string; desc: string };
  times: any[]; 
}

@Component({
  selector: 'app-acessibilidade',
  templateUrl: './acessibilidade.component.html',
  standalone: false
})
export class AcessibilidadeComponent implements OnInit {
  @ViewChild('top') top: ElementRef | undefined;

  // ---CMS---
  language: string = '';
  defaultLanguage: string = environment.defaultLanguage;
  accessibility_content: string = 'linhas-acessibilidade';
  accessibilityInfo: any = {};
  accessibilityInfoReady: boolean = false;

  // Configurações ngx-intl-tel-input
  SearchCountryField = SearchCountryField;
  CountryISO = CountryISO;
  PhoneNumberFormat = PhoneNumberFormat;
  preferredCountries: CountryISO[] = [CountryISO.Portugal, CountryISO.Spain, CountryISO.France];

  // Configurações Datepicker
  bsValueDay = new Date();
  bsConfig: Partial<BsDatepickerConfig> = {};
  minDate  = new Date();
  maxDate = new Date();

  fileError: string | null = null;
  selectedFiles: File[] = [];
  fileErrorMax: string | null = null;
  fileErrorMandatory: boolean = false;
  errorOnFiles: boolean = false;
  file: string = '';
  sizeOver: string = '';
  alreadyExists: string = '';
  notAllowed: string = '';
  allowedExtensions: any[string] = ['pdf', 'jpg', 'jpeg', 'png', 'doc', 'docx', 'xls', 'xlsx'];
  municipalities: any[] = [];
  lines: any[] = []
  selectedLine: string = ''
  line: string = '';
  lineOperator: string = '';
  lineOperatorLogos: any[] = [];
  unknownOperator: boolean = false;
  
  stops: any[] = [];
  hours: any[] = [];

  formAderir!: FormGroup;
  showFormAderir: boolean = true;
  submittedAderir: boolean = false;
  inProgressAderir: boolean = false;
  successAderir: boolean = false;
  errorAderir: boolean = false;

  formValidar!: FormGroup;
  showFormValidar: boolean = true;
  disableButtonValidar: boolean = false;
  submittedValidar: boolean = false;
  inProgressValidar: boolean = false;
  errorValidar: boolean = false;

  formReservar!: FormGroup;
  showFormReservar: boolean = false;
  submittedReservar: boolean = false;
  inProgressReservar: boolean = false;
  successReservar: boolean = false;
  errorReservar: boolean = false;
  errorReservarServer: string = '';

  lowerLimitInDays: number = 2;
  upperLimitInDays: number = 8;
  errorWorkingHours: boolean = false;

  public erroCadeiraRodas: boolean = false;

  private token: string = '';

  addedMemberId: string = '';

  public validatedMember: any;

  constructor(
    private recaptchaV3Service: ReCaptchaV3Service,
    private apiService: ApiService,
    private formBuilder: FormBuilder,
    private cmsService: CmsService,
    private route: ActivatedRoute,
    private router: Router,
    private translate: TranslateService,
    private externalService: ExternalService,
    private utilsService: UtilsService,
    private title: Title

  ) {
    let lang = localStorage.getItem('language') ?? this.defaultLanguage;
    this.language = lang.split('-')[0];

    this.translate.get('navLinks').pipe().subscribe(trans => {
      let pageTitle =  trans['linhas'] + ' | ' + trans['acessibilidade']
      this.title.setTitle(pageTitle);
    })

    this.configureDatepicker();
  }

  ngOnInit(){
    this.getContent();
    this.initForms();
    
    this.getRecaptchaToken();

    const captchaInterval = setInterval(() => {
      this.getRecaptchaToken();
    }, 110000);
  }

  getRecaptchaToken() {
    this.recaptchaV3Service.execute('importantAction')
      .subscribe((token: string) => {
        this.token = token;
      });
  }

  getContent(){
    this.cmsService.getContentList(this.accessibility_content, this.language).subscribe(
      {
        next: data => {
          this.accessibilityInfo = data['body']['data'];
          this.accessibilityInfoReady = true;
          
        },
        error: er => {
          this.router.navigate(['/500']);
        }
      }
    );
  }

  initForms(): void {
    // FORMULÁRIO ADERIR
    this.formAderir = this.formBuilder.group({
      firstName: ['', [Validators.required, Validators.pattern(/^\S.*$/)]],
      lastName:  ['', [Validators.required, Validators.pattern(/^\S.*$/)]],
      phone:     [undefined, [Validators.required]], // O componente intl trata o pattern internamente se configurado
      nif:       ['', [Validators.required, Validators.pattern(/^[0-9]{9}$/), this.vatValidator()]], // Simplificado para 9 dígitos standard
      email:     ['', [Validators.required, Validators.email]],
      necessitaCadeiraRodas: [null, [Validators.required]],
      files:     ['', [Validators.required]],
      acceptTerms: [false, [Validators.requiredTrue]],
      acceptTermsAccessibility: [false, [Validators.requiredTrue]]
    });

    // FORMULÁRIO VALIDAÇÂO
    this.formValidar = this.formBuilder.group({
      // Dados da Reserva
      vat: ['', [Validators.required, Validators.pattern(/^[0-9]{9}$/), this.vatValidator()]],
      code:  ['', [Validators.required, Validators.minLength(6), Validators.maxLength(6),Validators.pattern(/^[A-Z0-9]{6}$/)]],
    });

    // FORMULÁRIO RESERVAR
    this.formReservar = this.formBuilder.group({
      // Dados Pessoais (ReadOnly/Disabled)
      firstName: [{ value: '', disabled: true }],
      lastName:  [{ value: '', disabled: true }],
      email:     [{ value: '', disabled: true }],
      phone:     [{ value: '', disabled: true }],
      nif:       [{ value: '', disabled: true }],

      // Dados da Reserva
      municipality: ['', [Validators.required]],
      line:         ['', [Validators.required]],
      stop:         ['', [Validators.required]],
      hour:         ['', [Validators.required]],
      date:         [this.utilsService.addWorkDays(new Date(), this.lowerLimitInDays), [Validators.required]],

      // Consentimentos
      acceptTermsReserve: [false, [Validators.requiredTrue]],
      acceptTermsAccessibilityReserve: [false, [Validators.requiredTrue]],
    });

    this.onChanges()
  }

  vatValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;

      // If the field is empty, pass validation (let Validators.required handle empty fields)
      if (!value) {
        return null;
      }

      // Reuse the logic from the validation function
      const isValid = this.validateVat(value); 

      // If valid, return null. If invalid, return an error object.
      return isValid ? null : { invalidNif: true };
    };
  }

  validateVat(vat: string | number): boolean | undefined {
    const vatStr = String(vat).trim();

    if (!/^\d{9}$/.test(vatStr)) {
      return undefined;
    }

    const validFirstDigits = ['1', '2', '3', '5', '6', '8', '9'];
    const validFirstTwoDigits = ['45', '70', '71', '72', '74', '75', '77', '79'];

    const firstDigit = vatStr[0];
    const firstTwoDigits = vatStr.substring(0, 2);

    if (!validFirstDigits.includes(firstDigit) && !validFirstTwoDigits.includes(firstTwoDigits)) {
      return false;
    }

    let sum = 0;
    for (let i = 0; i < 8; i++) {
      sum += Number(vatStr[i]) * (9 - i);
    }

    const rest = sum % 11;
    const controlDigit = (rest === 0 || rest === 1) ? 0 : 11 - rest;

    return Number(vatStr[8]) === controlDigit;
  }

  onChanges(){
    this.formAderir.get('necessitaCadeiraRodas')?.valueChanges.subscribe(
      val => {
        this.erroCadeiraRodas = val === false;
      }
    )

    this.formValidar.get('code')?.valueChanges.subscribe((value: string) => {
      if (value) {
        this.formValidar.get('code')?.setValue(value.toUpperCase(), { emitEvent: false });
      }
    });

    this.formReservar.get('municipality')?.valueChanges.subscribe(
      val => {
        this.lineOperator = '';
        this.formReservar.get('line')?.setValue('');
        this.formReservar.get('stop')?.setValue('');

        if(val != '') {
          this.listRoutesByMunicipality(val);
        } else {
          this.lines.splice(0);
          this.stops.splice(0); 
        }
      }
    )

    this.formReservar.get('line')?.valueChanges.subscribe(
      val => {
        this.lineOperator = ''
        this.lineOperatorLogos = [];
        if(val != ''){
          this.formReservar.get('stop')?.setValue('');

          let ln = this.lines.find(ln => ln.codeCIM === val)

          if (ln != undefined) {
            this.line = ln

            if(ln['operator'] == 'DESCONHECIDO' || ln['operator'] == ''){
              this.lineOperator = 'CIMTS'
              this.unknownOperator = true;
            } else {

              this.lineOperatorLogos = ln['operatorLogos'];
              this.unknownOperator = false;
              switch (true) {
                case ln['operator'].toLowerCase().startsWith("transdev"):
                  this.lineOperator = 'TRD';
                  break;
                case ln['operator'].toLowerCase().startsWith("expressdiagram"):
                  this.lineOperator = 'EXP';
                  break;
                case ln['operator'].toLowerCase().startsWith("etapas"):
                  this.lineOperator = 'ETR';
                  break;
                case ln['operator'].toLowerCase().startsWith("remote"):
                  this.lineOperator = 'ETR';
                  break;
                case ln['operator'].toLowerCase().startsWith("rota"):
                  this.lineOperator = 'RDR';
                  break;
                default:
                  this.lineOperator = 'CIMTS';
              }
            }

            this.selectedLine = ln['codeCIM'];
            this.listStopsByRoute(this.selectedLine);
          }
          
        }
      }
    )

    this.formReservar.get('stop')?.valueChanges.subscribe(
      val => {

        if(val != null && val != '' && this.formReservar.get('date')?.value != ''){
          let stop = val;
          let date = this.utilsService.formatDate(this.formReservar.get('date')?.value);

          this.getStopScheduleDay(stop, date)
        }
      }
    )

    this.formReservar.get('date')?.valueChanges.subscribe(
      val => {

        if(val != null && val != '' && this.formReservar.get('stop')?.value != ''){
          this.errorWorkingHours = false;
          let stop = this.formReservar.get('stop')?.value;
          let date = this.utilsService.formatDate(val);

          this.getStopScheduleDay(stop, date)
        }
      }
    )

    this.formReservar.get('hour')?.valueChanges.subscribe(
      val => {
        if(val != null && val != '' && this.formReservar.get('date')?.value != ''){
          this.errorWorkingHours = false;

          let day = this.formReservar.get('date')?.value
          let hour = val

          const target = new Date(day);
          target.setHours(0, 0, 0, 0);

          const [hours, minutes] = hour.split(':').map(Number);
          target.setHours(hours, minutes);

          const totalWorkingHoursUntilReservation = this.utilsService.getWorkingHours(new Date(), target);

          let lowerLimitInHours = this.lowerLimitInDays * 24;
          let upperLimitInHours = this.upperLimitInDays * 24;

          this.errorWorkingHours = totalWorkingHoursUntilReservation < lowerLimitInHours || totalWorkingHoursUntilReservation > upperLimitInHours
        }
      }
    )
  }

  get fA(): { [key: string]: AbstractControl } {
    return this.formAderir.controls;
  }
  get fRV(): { [key: string]: AbstractControl } {
    return this.formValidar.controls;
  }
  get fR(): { [key: string]: AbstractControl } {
    return this.formReservar.controls;
  }

  configureDatepicker(): void {
    this.bsConfig = {
      dateInputFormat: 'DD-MM-YYYY',
      showWeekNumbers: false,
      isAnimated: true,
      minDate: this.utilsService.addWorkDays(new Date(), this.lowerLimitInDays),
      maxDate: this.utilsService.addWorkDays(new Date(), this.upperLimitInDays)
    };
  }

  onResetAderir(): void {
    this.submittedAderir = false;
    this.formAderir.reset();
  }

  onResetValidar(): void {
    this.submittedValidar = false;
    this.formValidar.reset();
    this.validatedMember = {};
  }

  onResetReservar(): void {
    this.submittedReservar = false;
    this.formReservar.reset();
  }

  onFileChangeReserves(event: Event): void {
    const target = event.target as HTMLInputElement;
    if (!target.files) return;
    const files = Array.from(target.files);
    this.fileError = '';
    this.fileErrorMax = '';
    let validFiles: File[] = []

    files.forEach(file => {
      if (file.size > 5 * 1024 * 1024) {
        this.fileError +=  `"${this.file}${file.name}${this.sizeOver}"<br>`;
      } else {
        const exists = this.selectedFiles.some(
          selectedFile => selectedFile.name === file.name && selectedFile.size === file.size
        );
        if (exists) {
          this.fileError += `"${this.file}${file.name}${this.alreadyExists}"<br>`;
        } else {
          const fileExtension = file.name.split('.').pop()?.toLowerCase();
          if (!fileExtension || !this.allowedExtensions.includes(fileExtension)) {
            this.fileError += `"${this.file}${file.name}${this.notAllowed}"<br>`;
            target.value = ''; // Clear input
          } else {
            validFiles.push(file)
          }
        }
      }
    })

    validFiles.forEach(file => {
      this.selectedFiles.push(file);
    })
  }

  onFileChangeReservesUnique(event: Event): void {
    const target = event.target as HTMLInputElement;
    if (!target.files) return;
    const files = Array.from(target.files);
    this.fileError = '';
    this.fileErrorMax = '';
    let validFiles: File[] = []

    this.selectedFiles = []

    files.forEach(file => {
      if (file.size > 5 * 1024 * 1024) {
        this.fileError +=  `"${this.file}${file.name}${this.sizeOver}"<br>`;
      } else {
        const exists = this.selectedFiles.some(
          selectedFile => selectedFile.name === file.name && selectedFile.size === file.size
        );
        if (exists) {
          this.fileError += `"${this.file}${file.name}${this.alreadyExists}"<br>`;
        } else {
          const fileExtension = file.name.split('.').pop()?.toLowerCase();
          if (!fileExtension || !this.allowedExtensions.includes(fileExtension)) {
            this.fileError += `"${this.file}${file.name}${this.notAllowed}"<br>`;
            target.value = ''; // Clear input
          } else {
            validFiles.push(file)
          }
        }
      }
    })

    validFiles.forEach(file => {
      this.selectedFiles.push(file);
    })
  }

  removeFile(file: File): void {
    this.selectedFiles = this.selectedFiles.filter(
      selectedFile => !(selectedFile.name === file.name && selectedFile.size === file.size)
    );
  }

  onSubmitAderir(): void {
    this.submittedAderir = true;
    this.inProgressAderir = true;
    this.successAderir = false;
    this.errorAderir = false;
    this.fileErrorMax = '';
    this.fileErrorMandatory = false;

    if (this.formAderir.invalid || this.selectedFiles.length == 0) {
      if(this.selectedFiles.length == 0){
        this.fileErrorMandatory = true;
      }
      this.inProgressAderir = false;
      return;
    } else {
      let params = {
        first_name: this.formAderir.get('firstName')?.value.trim(),
        last_name: this.formAderir.get('lastName')?.value.trim(),
        vat: this.formAderir.get('nif')?.value.trim(),
        email: this.formAderir.get('email')?.value.trim(),
        phone: this.formAderir.get('phone')?.value['e164Number'].trim(),
      }

      this.apiService.createMember(params, this.token).subscribe(
        data => {
          this.addedMemberId = data['body']['id'];
          this.onSubmitFileAderir(this.addedMemberId);
        },
        error => {
          this.errorAderir = true;
          this.submittedAderir = false;
          this.inProgressAderir = false;
          this.getRecaptchaToken();
          this.formAderir.get('acceptTerms')?.setValue(false);
          this.formAderir.get('acceptTermsAccessibility')?.setValue(false);
        }
      )
    }
  }

  onSubmitFileAderir(memberCode: string){
    try {
      this.errorOnFiles = false;
      let filesToAdd = this.selectedFiles.length;
      let filesAdded = 0;

      if(filesToAdd > 0) {
        for (const file of this.selectedFiles) {
          const fileFormData = new FormData();

          const renamedFile = new File([file], memberCode + '/open_' + file.name, { type: file.type });

          fileFormData.append('file', renamedFile);
          fileFormData.append('name', 'compaints/' + renamedFile.name);

          const type = 'REQUESTS';

          this.apiService.createFile(fileFormData, type)
            .subscribe({
              next: data => {
                filesAdded++;
                if(filesAdded == filesToAdd){
                  this.selectedFiles = [];
                  this.inProgressAderir = false;
                  this.successAderir = true; 
                  this.showFormAderir = false;
                  this.top?.nativeElement.scrollIntoView({ block: 'center', behavior: 'smooth' });
                  this.formAderir.reset();
                  setTimeout(() => {
                    this.submittedAderir = false;
                    this.successAderir = false; 
                    this.addedMemberId = '';
                    window.location.reload()
                  }, 5000);
                }

              },
              error: er => {
                filesAdded++;
                if(filesAdded == filesToAdd){
                  this.selectedFiles = [];
                  this.errorOnFiles = true;
                  this.inProgressAderir = false;
                  this.successAderir = true; 
                  this.showFormAderir = false;
                  this.top?.nativeElement.scrollIntoView({ block: 'center', behavior: 'smooth' });
                  this.formAderir.reset();
                  setTimeout(() => {
                    this.submittedAderir = false;
                    this.successAderir = false; 
                    this.addedMemberId = '';
                    window.location.reload()
                  }, 5000);
                }
              }
            });

        }
      } else {
        this.selectedFiles = [];
        this.errorOnFiles = false;
        this.inProgressAderir = false
        this.successAderir = true; 
        this.showFormAderir = false;
        this.top?.nativeElement.scrollIntoView({ block: 'center', behavior: 'smooth' });
        this.formAderir.reset();
        setTimeout(() => {
          this.successAderir = false;
          this.addedMemberId = '';
          window.location.reload()
        }, 5000);
      }

    } catch (error) {
      console.error('Error uploading files', error);
    }
  }

  onSubmitValidar(): void {
    this.disableButtonValidar = true;
    this.submittedValidar = true;
    this.inProgressValidar = true;
    this.errorValidar = false;
    this.fileErrorMax = '';

    if (this.formValidar.invalid) {
      this.inProgressValidar = false;
      this.disableButtonValidar = false;
      return;
    } else {
      let params = {
        vat: this.formValidar.get('vat')?.value.trim(),
        code: this.formValidar.get('code')?.value.trim()
      }

      this.apiService.validateMember(params, this.token).subscribe(
        data => {
          
          if(data.body['status'] != undefined && data.body['status'] == 'APPROVED') {
            this.validatedMember = data.body
            this.validatedMember['vat'] = params['vat']
            this.inProgressValidar = false;
            this.showFormValidar = false;
            this.showFormReservar = true;
            this.getRecaptchaToken();
            this.getMunicipalities();
          } else {
            this.validatedMember = {}
            this.errorValidar = true;
            this.inProgressValidar = false;
            setTimeout(() => {
              this.submittedValidar = false;
              this.disableButtonValidar = false;
              this.errorValidar = false;
              this.getRecaptchaToken();
            }, 3000);
          }

        },
        error => {
          this.validatedMember = {}
          this.errorValidar = true;
          this.inProgressValidar = false;
          setTimeout(() => {
            this.submittedValidar = false;
            this.disableButtonValidar = false;
            this.errorValidar = false;
            this.getRecaptchaToken();
          }, 3000);
        }
      )
    }
  }

  onBackReservar(): void {
    this.validatedMember = {}
    this.formValidar.reset()
    this.showFormValidar = true;
    this.showFormReservar = false;
    this.submittedValidar = false;
    this.inProgressValidar = false;
  }

  getMunicipalities(){
    this.externalService.getMunicipalities().subscribe(
        data => {

          this.municipalities.splice(0)
          
          data.forEach(element => {
            let mun = {
              name: element['pDescricao'],
              code: element['pDescricao'],
              key: element['pKey'],
              region: element['pNUTIII']
            }

            this.municipalities.push(mun)
          });

          this.municipalities = this.municipalities.sort((a: any, b: any) => a.name.localeCompare(b.name, 'pt', { sensitivity: 'base' }) );
        },
        error => {
          this.router.navigate(['/500']);
        } 
      )
  }

  listRoutesByMunicipality(municipality: string){
    this.lines.splice(0);

    this.externalService.getRoutesByMunicipality(municipality).subscribe(
      {
        next: data => {
          data.forEach(item => {
            let opCode = '';
            let opLogos = [];
            switch (true) {
              case (item['pdesigOperador'] as any).toLowerCase().startsWith("transdev"):
                opCode = 'Transdev Norte';
                opLogos.push('transdev');
                break;
              case (item['pdesigOperador'] as any).toLowerCase().startsWith("expressdiagram"):
                opCode = 'Valpi, Rodonorte';
                opLogos.push('valpi');
                opLogos.push('rodonorte');
                break;
              case (item['pdesigOperador'] as any).toLowerCase().startsWith("etapas"):
                opCode = 'AVPacense, Valpi';
                opLogos.push('avpacense');
                opLogos.push('valpi');
                break;
              case (item['pdesigOperador'] as any).toLowerCase().startsWith("rota"):
                opCode = 'Rodonorte, AVLandim';
                opLogos.push('rodonorte');
                opLogos.push('avlandim');
                break;
              default:
                opCode = '';
            }
            let line = {
              number: item['pCodCarOperador'],
              name: item['pDesignacao'],
              municipality: municipality,
              operator: item['pdesigOperador'],
              operatorCode: opCode,
              operatorLogos: opLogos,
              codeCIM: item['pCodCarCIM'],
              color: item['pColorHex']
            }

            this.lines.push(line);
          });
          
        },
        error: er => {
          this.router.navigate(['/500']);
        }
      }
    )
  }

  listStopsByRoute(route_id: string){
    this.stops.splice(0);

    let params = {  
      "offset": 0,
      "limit": 2000,
      "include": true,
      route_ids: [route_id]
    }

    this.apiService.getStopsByRoute(params).subscribe(
      {
        next: data => {
          let results = data['body']['results'];
          
          results.forEach((item: any) => {
            let stop = {
              id: '1:' + item['stop_id'],
              code: item['stop_id'],
              name: item['stop_name'],
              municipality: '',
              lat: item['lat'],
              lng: item['lon'],
              zone: item['zone_id']
            }

            this.stops.push(stop);

            this.stops.sort((a: any, b: any) => a.name < b.name ? -1 : 1 );
          });
        },
        error: er => {
          this.router.navigate(['/500']);
        }
      }
    )
  }

  getStopScheduleDay(stop: string, day: string){
    this.hours = []
    if(stop != '' && day != ''){
      this.externalService.getStopScheduleDay(stop,day).subscribe(
        {
          next: data => {
            let pattern = ':' + this.selectedLine + ':'

            const response = data as ScheduleData[];

            response.forEach((entry) => {
              if (entry.pattern.id.includes(pattern)) {

                if (entry.times.length > 0) {

                  entry.times.forEach((element: any) => {
                    let time = {
                      realtimeArrival: element['realtimeArrival'],
                      realtimeArrivalDesc: this.utilsService.secondsToTime(element['realtimeArrival']),
                    }

                    this.hours.push(time)
                  });
                  
                }
              }
            });

            this.hours.sort((a,b) => (a.realtimeArrival > b.realtimeArrival) ? 1 : -1 )

          },
          error: er => {

          }
        }
      )
    }
  }

  onSubmitReservar(): void {
    this.submittedReservar = true;
    this.inProgressReservar = true;
    this.successReservar = false;
    this.errorReservar = false;
    this.errorReservarServer = '';

    if (this.formReservar.invalid) {
      this.inProgressReservar = false;
      return;
    } else {
      let date = new Date(this.formReservar.get('date')?.value).toISOString().split('T')[0] + 'T' + this.formReservar.get('hour')?.value + ':00Z'
      let dateSimple = new Date(this.formReservar.get('date')?.value).toISOString().split('T')[0]
      let mun = this.municipalities.find(mun => mun.code === this.formReservar.get('municipality')?.value)
      let municipality = mun != undefined ? mun['name'] : ''
      let stop = this.stops.find(stop => stop.id === this.formReservar.get('stop')?.value)
      let stopNumName = stop['code'] + ' - '+ stop['name']
      let lineNumName = (this.line as any)['number'] + ' - '+ (this.line as any)['name']

      let body = 'Reserva para ' + dateSimple + ', às ' + this.formReservar.get('hour')?.value
        + ', na paragem ' +  stopNumName + ' da linha ' + lineNumName + ' (' + municipality + ').'

      let params = {
        requested_by_name: this.validatedMember.first_name + ' ' + this.validatedMember.last_name,
        requested_by_email: this.validatedMember.email,
        requested_by_id: this.validatedMember.user_id,
        ref: this.lineOperator + '##' + (this.line as any)['number'],
        type: 'RESERVATION',
        ref_type: 'OPERATOR',
        subject: 'Reserva',
        body: body,
        occurrence_date: date,
        extra:{
            phone: this.validatedMember.phone,
            time: this.formReservar.get('hour')?.value,
            municipality: municipality,
            line: lineNumName,
            stop: stopNumName,
            vat: this.validatedMember.vat
          }
      }

      this.apiService.createRequest(params, this.token).subscribe(
        data => {
          this.showFormReservar = false;
          this.submittedReservar = false;
          this.inProgressReservar = false;
          this.successReservar = true;
          this.top?.nativeElement.scrollIntoView({ block: 'center', behavior: 'smooth' });
          this.formReservar.reset();
          setTimeout(() => {
            this.successReservar = false;
            window.location.reload()
          }, 5000);

        },
        error => {
          if(error?.error?.detail?.description?.[this.language]) this.errorReservarServer = error?.error?.detail?.description?.[this.language]
          
          this.errorReservar = true;
          this.submittedReservar = false;
          this.inProgressReservar = false;
          this.formReservar.get('acceptTerms')?.setValue(false, {emit: false});
          this.formReservar.get('acceptTermsAccessibility')?.setValue(false, {emit: false});
        }
      )

      
    }
  }
}
